import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterHeader from '../FilterHead';
import RangeSlider from './RangeSlider';
import RowSection from './RowSection';
import { getAppliedFilterValue } from '../../../../../helpers/utils';
import OmnitureUtils from '../../../../../../common/utils/Omniture/OmnitureUtils';

import './styles.scss';

const BATCH_SIZE = 4;
const totalBatches = 3;

const RangeFilter = ({
  filterData,
  handleFilterChange,
  showOverlayFilterLoader,
  filterIndex,
  isCollapsedOnInit = false,
}) => {
  const [isCollapsed, setCollapsed] = useState(!isCollapsedOnInit);
  const [shownFiltersSize, setShownFiltersSize] = useState(BATCH_SIZE);
  const [search] = useSearchParams();

  const hasMoreOptions = useMemo(() => {
    const optionsLength = filterData?.listingFilterRecommendedValues?.length;

    return optionsLength > shownFiltersSize;
  }, [filterData, shownFiltersSize]);

  const increaseBatchSize = useCallback(() => {
    setShownFiltersSize(currentValue => {
      if (currentValue >= totalBatches * BATCH_SIZE) {
        return filterData?.listingFilterRecommendedValues?.length;
      }

      return currentValue + BATCH_SIZE;
    });
  }, [filterData]);

  const handleCollapsed = () => {
    setCollapsed(!isCollapsed);
  };

  const [min, max, stepSize, prefix, suffix] = useMemo(() => {
    let min = 0;
    let max = 0;
    let stepSize = 1;
    const filterValues = filterData?.listingFilterRecommendedValues || [];
    if (filterValues.length) {
      const firstFilterValue = filterValues[0];
      const lastFilterValue = filterValues[filterValues.length - 1];

      min = +(firstFilterValue.uniqueId?.split('_')?.[0] || 0);
      max = +(lastFilterValue.uniqueId?.split('_')?.[1] || 0);
    } else {
      const firstListingFilterValue = filterData?.listingFilterValues?.[0]?.uniqueId;
      if (firstListingFilterValue?.indexOf('_') > -1) {
        [min, max] = firstListingFilterValue.split('_');
      } else {
        min = filterData?.listingFilterValues?.[0]?.uniqueId;
        max =
          filterData?.listingFilterValues?.[(filterData?.listingFilterValues?.length || 0) - 1]
            ?.uniqueId;
      }
    }

    const { prefix, suffix } = filterData?.listingFilterValues?.[0] || {};

    if (+max > 1000) {
      stepSize = 1000;
      if (max % 1000) {
        max += 1000 - (max % 1000);
      }
    }

    return [+min, +max, stepSize, prefix, suffix];
  }, [filterData]);

  const appliedValues = useMemo(
    () => getAppliedFilterValue(filterData?.urlParam, search),
    [filterData?.urlParam, search]
  );

  const handleOptionClick = useCallback(
    (values, { prop54 } = {}) => {
      OmnitureUtils.trackClickEvent({
        prop54: prop54.replace('<urlParam>', filterData?.urlParam),
      });
      handleFilterChange({ urlParam: filterData?.urlParam, values });
    },
    [handleFilterChange]
  );

  const appliedRange = useMemo(() => {
    if (appliedValues.length) {
      const [smallest, largest] = appliedValues?.[0]?.split('_').map(val => +val) || [];

      return [smallest || 0, largest || 0];
    }

    return [min || 0, max || 0];
  }, [min, max, appliedValues]);

  return (
    <div className="filtersOuter">
      {showOverlayFilterLoader && <div className="filtersOverlayLoader" />}
      <FilterHeader
        isCollapsed={isCollapsed}
        handleCollapsed={handleCollapsed}
        name={filterData?.name}
        filterIndex={filterIndex}
      />

      {isCollapsed && (
        <div className="innerListSection">
          <RangeSlider
            min={min || 0}
            max={max || 0}
            stepSize={stepSize}
            prefix={prefix}
            suffix={suffix}
            applyRange={handleOptionClick}
            appliedRange={appliedRange}
            filterUrlParam={filterData?.urlParam}
          />
          {filterData.listingFilterRecommendedValues
            ?.slice(0, shownFiltersSize)
            ?.map(filterOption => (
              <RowSection
                key={filterOption?.uniqueId}
                filterOption={filterOption}
                filterStats={filterData?.filterStats?.discreteStats}
                handleOptionClick={handleOptionClick}
                appliedValues={appliedValues}
                filterUrlParam={filterData?.urlParam}
              />
            ))}
          {hasMoreOptions && (
            <p>
              <span onClick={increaseBatchSize} className="linkText boldFont">
                Show More
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default RangeFilter;
